import type { TFunction } from 'next-i18next';
import { LOCALE_COMMON_PATHS } from 'types/locales';

/** The English unit the listing-card producers append ("1000–1200 sqft", "N/A sqft"). */
const ENGLISH_UNIT = /sqft$/;
/** The value those producers emit when the bounds are present but empty. */
const NOT_AVAILABLE = 'N/A';

/**
 * Translates a square-footage label that arrives pre-formatted in English.
 *
 * Several producers (`getSqftLabel`, `AreaPageListingCardData.sqftLabel`, …) build
 * the label themselves and bake in the English unit. They also carry a min–max
 * range, which `listing_card.number_of_sqft` cannot express since it takes a
 * single count. So the formatted range is kept as-is and only the unit is swapped
 * for its translation; the "N/A" placeholder maps to the key's zero form instead.
 *
 * Labels without the English unit — the "-" placeholder, an empty string — pass
 * through unchanged. Moving the producers onto `SquareFootage` +
 * `formatSquareFootage` would make this unnecessary.
 */
export function localizeSqftLabel(label: string, t: TFunction): string {
  if (!ENGLISH_UNIT.test(label)) {
    return label;
  }

  if (label.replace(ENGLISH_UNIT, '').trim() === NOT_AVAILABLE) {
    return t(LOCALE_COMMON_PATHS.LISTING_CARD_NUMBER_OF_SQFT, { count: 0, defaultValue: 'N/A sqft' });
  }

  const unit = t(LOCALE_COMMON_PATHS.LISTING_CARD_NUMBER_OF_SQFT, { count: 1, defaultValue: '1 sqft' }).replace('1', '').trim();
  return label.replace(ENGLISH_UNIT, unit);
}
